// src/components/StatsDashboard.js
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { firestore } from '../firebase';
import { calculateBadges, ALL_BADGES, computeRawStats } from './BadgeEngine';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import './StatsDashboard.css';

const BoxScore = ({ frames }) => {
  const displayRoll = (f, r, framesArr) => {
    const val = framesArr[f][`roll${r + 1}`];
    if (!val) return "";
    const v = parseInt(val, 10);
    const first = parseInt(framesArr[f].roll1, 10) || 0;
    if (f < 9) {
      if (r === 0) return v === 10 ? "X" : val;
      return (first + v === 10) ? "/" : val;
    } else {
      if (r === 0) return v === 10 ? "X" : val;
      if (r === 1) return (v === 10) ? "X" : (first < 10 && first + v === 10 ? "/" : val);
      if (r === 2) {
        const second = parseInt(framesArr[f].roll2, 10) || 0;
        return (v === 10) ? "X" : (second < 10 && second + v === 10 ? "/" : val);
      }
    }
    return val;
  };
  return (
    <div className="box-score-expanded">
      <div className="frames-grid">
        {frames.map((f, i) => (
          <div key={i} className="mini-frame">
            <div className="mini-rolls"><span>{displayRoll(i, 0, frames)}</span><span>{displayRoll(i, 1, frames)}</span>{i === 9 && <span>{displayRoll(i, 2, frames)}</span>}</div>
            <div className="frame-num">{i + 1}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function StatsDashboard() {
  const { userId: urlUserId } = useParams();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGame, setExpandedGame] = useState(null);
  const [viewedUser, setViewedUser] = useState(null);

  const targetUserId = urlUserId || currentUser?.uid;
  const isOwnProfile = !urlUserId || urlUserId === currentUser?.uid;

  useEffect(() => {
    if (!targetUserId) return;
    const loadGames = async () => {
      setLoading(true);
      try {
        const q = query(collection(firestore, 'games'), where('userId', '==', targetUserId), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt instanceof Timestamp ? d.data().createdAt.toDate() : new Date() }));
        setGames(results);
        if (results.length > 0) setViewedUser(results[0].userName || "Bowler");
        const gId = new URLSearchParams(location.search).get('gameId');
        if (gId) setExpandedGame(gId);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadGames();
  }, [targetUserId, location]);

  if (loading) return <div className="loading">Syncing...</div>;

  // USE THE SHARED ENGINE
  const s = computeRawStats(games);
  const earnedBadgeIds = calculateBadges(games);

  return (
    <div className="stats-dashboard-container">
      <h1 className="neon-title">{isOwnProfile ? "My Performance" : `${viewedUser}'s Stats`}</h1>
      <div className="trophy-case">
        <div className="trophy-grid">
          {ALL_BADGES.map(b => (
            <div key={b.id} className={`trophy-item ${earnedBadgeIds.includes(b.id) ? 'earned' : 'locked'}`}>
              <span className="trophy-icon">{b.icon}</span><span className="trophy-label">{b.label}</span><span className="trophy-description">{b.description}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="stats-hero">
        <div className="hero-stat"><span className="label">Average</span><span className="value neon-blue-text">{(s.totalScore / (games.length || 1)).toFixed(1)}</span></div>
        <div className="hero-stat"><span className="label">Best Game</span><span className="value neon-pink-text">{games.length > 0 ? Math.max(...games.map(g => g.totalScore)) : 0}</span></div>
      </div>
      <div className="pro-stats-grid">
        <div className="pro-stat-card"><span className="pro-label">Strike %</span><span className="pro-value">{((s.strikes/s.totalFrames)*100 || 0).toFixed(1)}%</span></div>
        <div className="pro-stat-card"><span className="pro-label">Spare %</span><span className="pro-value">{((s.spares/s.spareOpportunities)*100 || 0).toFixed(1)}%</span></div>
        <div className="pro-stat-card"><span className="pro-label">Single Pin %</span><span className="pro-value text-gold">{((s.singlePinConversions/s.singlePinLeaves)*100 || 0).toFixed(1)}%</span></div>
        <div className="pro-stat-card"><span className="pro-label">Multi Pin %</span><span className="pro-value text-gold">{((s.multiPinConversions/s.multiPinLeaves)*100 || 0).toFixed(1)}%</span></div>
      </div>
      <div className="game-history-list">
        {games.map(g => (
          <div key={g.id} className={`history-group ${expandedGame === g.id ? 'is-expanded' : ''}`}>
            <div className="history-card" onClick={() => setExpandedGame(expandedGame === g.id ? null : g.id)}>
              <div className="history-info"><span>{g.createdAt.toLocaleDateString()}</span><span className="score">{g.totalScore}</span></div>
            </div>
            {expandedGame === g.id && <BoxScore frames={g.frames} />}
          </div>
        ))}
      </div>
    </div>
  );
}